import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useHistory } from 'react-router-dom';
import { getEnv } from '../Context/env';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import { useMediaQuery } from '@nebula/hooks';
import MySubscriptionDesktopAndTablet from './MySubscriptionDesktopAndTablet';
import type { CurrentSubscriptionPlan, Feature, OrderedPlan, ProductPlan, ErrorState, SubscriptionData } from './types';
import EntitlementInfoModal from './shared/EntitlementInfoModal';
import MySubscriptionMobile from './MySubscriptionMobile';
// Mock data — replace with API call result (e.g. via Redux selector or useEffect + fetch).
import subscriptionDataJson from './mySubscriptionData.json';
import ManageSubscriptionDrawer from './shared/ManageSubscriptionDrawer';
import { useDrawer } from '@nebula/drawer';
import { isMobileApp, MobileLocations, navigateTo, redeemBreachVoucher } from '@ecs-uk/web-common-mobile-view-helpers';
import {
  flexProducts,
  getProductFamilyName,
  normalizeProductFamily,
  flexProductFamilies,
} from '../../constants/products';
import useFlexData from '../../hooks/flexHooks/useFlexData';
import GlobalStyles from '@nebula/global-styles';
import switchInfo, { Entitlements } from '../../helpers/flex-helpers/switchInfo';
import getDefermentInfo, { DefermentInfo as FlexDefermentInfo } from '../../helpers/flex-helpers/getDefermentInfo';
import { errorMessagesFlex, fulfillmentStatuses, subscriptionActions } from '../../constants';
import getDynamicString from '../../helpers/flex-helpers/getDynamicString';
import { getOrderedPlans } from './utils';
import { tabletMin } from './constants';
import usePageView from '../../hooks/usePageView';

const PAGE_NAME = 'ProdMoveMySubscriptionPage';

const subscriptionData = subscriptionDataJson as SubscriptionData;

type EntitlementsResponse = {
  active?: boolean;
  isInCoolingOffPeriod?: boolean;
  plans?: Array<{
    planCode: string;
    price?: number;
    termFrequency?: string | null;
    family?: string;
    trialLength?: number;
  }>;
  family?: string;
  planPrice?: number;
  defermentInfo?: FlexDefermentInfo;
  fulfillmentStatus?: string;
};

type ProductResponse = {
  isTrialEligible?: boolean;
  eligiblePlans: Array<ProductPlan>;
};

type ApiErrorState = {
  boost?: { error?: boolean };
  creditLock?: { error?: boolean };
  scores?: { error?: boolean };
};

const MySubscription = ({ isWebview = false }: { isWebview?: boolean }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    entitlements: entitlementsResponse,
    products: productsResponse,
    entitlementsError,
    isFlexDataReady,
  } = useFlexData();
  const entitlements = entitlementsResponse as EntitlementsResponse | undefined;
  const allProducts = productsResponse as ProductResponse;
  const showGenericError = useSelector((state: ApiErrorState) =>
    Boolean(state.boost?.error || state.creditLock?.error || state.scores?.error)
  );

  const defermentInfo = entitlements?.defermentInfo;
  const defermentMessage = getDefermentInfo(defermentInfo);
  const currentFamily = normalizeProductFamily(entitlements?.plans?.[0]?.planCode ?? '');
  const currentSubscription = entitlements?.plans?.[0]
    ? ({
        ...entitlements.plans[0],
        family: entitlements.plans[0].family ?? currentFamily,
      } as CurrentSubscriptionPlan)
    : undefined;

  const isActivatingBenefits = entitlements?.fulfillmentStatus !== fulfillmentStatuses.succeeded;

  const activatingBenefitsErrorHeading =
    getDynamicString('activatingBenefits', [getProductFamilyName(currentFamily)], errorMessagesFlex as any) ?? '';
  const activatingBenefitsErrorMessage = errorMessagesFlex.tryAgain;
  const isInCoolingOffPeriod = entitlements?.isInCoolingOffPeriod ?? false;
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const { isShowing, toggle } = useDrawer();

  const isTabletMin = useMediaQuery(tabletMin);

  usePageView(PAGE_NAME);

  const onBack = history.length > 1 ? () => history.goBack() : null;

  const onVoucher = useCallback(() => {
    const env = getEnv();
    const publicHost = env.REACT_APP_PUBLIC_HOST;
    if (isWebview && publicHost) {
      const url = new URL('/my-subscription', publicHost);
      redeemBreachVoucher(url);
    } else {
      history.push('/my-subscription');
    }
  }, [isWebview, history]);

  const onSelectPlan = async (plan: OrderedPlan) => {
    if (!plan.product || plan.isCurrent) return;

    const isCancellation = plan?.product?.planCode === flexProducts.free;
    const isIdPlus = plan?.product?.family === flexProductFamilies.identity;
    const isCreditExpert = plan?.product?.family === flexProductFamilies.credit_expert;

    if (isMobileApp()) {
      if (isCancellation) {
        navigateTo(MobileLocations.CANCEL_SUBSCRIPTION);
      } else if (isIdPlus) {
        navigateTo(MobileLocations.UPSELL_IDENTITY_PAYMENT);
      } else if (isCreditExpert) {
        navigateTo(MobileLocations.UPSELL_PAYMENT);
      }
    } else if (isCancellation) {
      dispatch(push('/cancel'));
    } else {
      const targetFamily = plan.family ?? plan.product.family;
      dispatch(push(`/switch?switchingTo=${targetFamily}`));
    }
  };

  // Do not render until entitlements are loaded and a active current plan can be derived.
  if (!isFlexDataReady || !entitlements || entitlementsError || !currentFamily || entitlements?.active === false) {
    return null;
  }

  const { billingDateToday, switchPermitted } = switchInfo(entitlements as Entitlements);
  const switchErrorMessage = billingDateToday ? errorMessagesFlex.subscriptions.billingDate : defermentMessage;
  const switchErrorHeading =
    defermentInfo?.type === subscriptionActions.cancellation
      ? getDynamicString('changeFailure.cancellation') ?? ''
      : getDynamicString('changeFailure.intro', ['subscription']) ?? '';
  const showSwitchError = !switchPermitted && Boolean(switchErrorMessage);
  // No plans to switch to, e.g. products API returned an empty list — keep the CTA disabled.
  const hasEligiblePlans = (allProducts?.eligiblePlans?.length ?? 0) > 0;
  const canSwitchPlan = switchPermitted && hasEligiblePlans;

  const orderedPlans = getOrderedPlans(
    allProducts?.eligiblePlans ?? [],
    subscriptionData,
    allProducts?.isTrialEligible,
    currentSubscription
  );

  const errorState: ErrorState = {
    genericError: {
      id: 'generic-error',
      show: showGenericError,
      heading: errorMessagesFlex.someDataMissing,
      message: errorMessagesFlex.technicalIssues,
    },
    activatingBenefits: {
      id: 'activating-benefits',
      show: isActivatingBenefits,
      heading: activatingBenefitsErrorHeading,
      message: activatingBenefitsErrorMessage,
    },
    switchBlocked: {
      id: 'switch-blocked',
      show: showSwitchError,
      heading: switchErrorHeading,
      message: switchErrorMessage,
    },
  };

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalStyles />
      <>
        {isTabletMin ? (
          <MySubscriptionDesktopAndTablet
            currentPlan={currentFamily}
            orderedPlans={orderedPlans}
            isWebview={isWebview}
            subscriptionData={subscriptionData}
            switchPermitted={canSwitchPlan}
            errorState={errorState}
            onSelectPlan={onSelectPlan}
            onBack={onBack}
            onSelectFeature={setSelectedFeature}
            toggleManageDrawer={toggle}
            onVoucher={onVoucher}
          />
        ) : (
          <MySubscriptionMobile
            currentPlan={currentFamily}
            orderedPlans={orderedPlans}
            isWebview={isWebview}
            subscriptionData={subscriptionData}
            switchPermitted={canSwitchPlan}
            errorState={errorState}
            onBack={onBack}
            onSelectPlan={onSelectPlan}
            onSelectFeature={setSelectedFeature}
            toggleManageDrawer={toggle}
            onVoucher={onVoucher}
          />
        )}
        <ManageSubscriptionDrawer
          isShowing={isShowing}
          toggleDrawer={toggle}
          isInCoolingOffPeriod={isInCoolingOffPeriod}
          currentSubscription={entitlements?.plans?.[0] ?? {}}
          onSelectPlan={onSelectPlan}
          allProducts={allProducts?.eligiblePlans ?? []}
          canChangeSubscription={switchPermitted}
          billingDateToday={billingDateToday}
          hasDeferment={Boolean(defermentInfo)}
        />
        {selectedFeature && <EntitlementInfoModal feature={selectedFeature} onClose={() => setSelectedFeature(null)} />}
      </>
    </ThemeProvider>
  );
};

export default MySubscription;
